import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Search, Calendar, MapPin, ChevronUp, ChevronDown, Map, Users, Wifi, X, Bell } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RideCard } from '../components/RideCard';
import { RideDetailsModal } from '../components/RideDetailsModal';
import { AdvancedFilters } from '../components/AdvancedFilters';
import { RideMap, RideMapPreview } from '../components/RideMap';
import MapPickerModal from '../components/MapPickerModal';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { RoutePriceEstimate } from '../components/RoutePriceEstimate';
import { RouteCorridorOverlay } from '../components/RouteCorridorOverlay';
import { RoundTripBundle, groupRoundTrips } from '../components/RoundTripBundle';
import speak from '../utils/tts';
import './RideSearch.css';

import { API as API_URL, BACKEND_URL } from '../utils/apiConfig';

export const RideSearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // Expanded address fields
  const [departure, setDeparture] = useState({ city: '', street: '', number: '' });
  const [arrival, setArrival] = useState({ city: '', street: '', number: '' });
  const [departureCoords, setDepartureCoords] = useState(null);
  const [arrivalCoords, setArrivalCoords] = useState(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  // Phase A1 — Datums-Toleranz auch für Passagiere
  const [dateToleranceDays, setDateToleranceDays] = useState(0);
  // Phase A2 — Pickup/Dropoff-Radius (Korridor-Suche aktivieren)
  const [pickupRadiusKm, setPickupRadiusKm] = useState(5);
  const [dropoffRadiusKm, setDropoffRadiusKm] = useState(5);
  const [useCorridor, setUseCorridor] = useState(true);
  // Phase B — Maximaler Umweg, den der Fahrgast akzeptiert (0–20 km)
  // 0 = use driver's corridor as-is (no extra cap)
  const [maxDetourKm, setMaxDetourKm] = useState(0);

  const [rides, setRides] = useState([]);
  const [filteredRides, setFilteredRides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);

  // MapPicker state
  const [mapPickerOpen, setMapPickerOpen] = useState(null); // 'departure' | 'arrival' | null

  // Phase A3 — Corridor route overlay
  const [corridorRideId, setCorridorRideId] = useState(null);

  // Live Socket.IO matching
  const [liveMatches, setLiveMatches] = useState([]);
  const [socketConnected, setSocketConnected] = useState(false);
  const socketRef = useRef(null);
  const [showLivePopup, setShowLivePopup] = useState(false);

  // Sidebar Filters State
  const [priceRange, setPriceRange] = useState([0, 50]);
  const [suitcaseAllowed, setSuitcaseAllowed] = useState(false);
  const [bicycleAllowed, setBicycleAllowed] = useState(false);
  const [skiAllowed, setSkiAllowed] = useState(false);

  // Notify Me state
  const [notifyMeLoading, setNotifyMeLoading] = useState(false);
  const [notifyMeSaved, setNotifyMeSaved] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [acRequired, setAcRequired] = useState(false);
  const [spaciousRequired, setSpaciousRequired] = useState(false);
  const [nonSmokerOnly, setNonSmokerOnly] = useState(false);
  const [petsAllowed, setPetsAllowed] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [minRating, setMinRating] = useState(1);

  // Advanced Filters State
  const [advancedFilters, setAdvancedFilters] = useState({
    luggage: 1, backpack: false, ski: false, bicycle: false,
    smoking: 'any', pets: 'any', music: 'any', conversation: 'any',
    vehicleType: '', ac: false, spacious: false, verifiedOnly: false, minRating: 4.0
  });

  const [collapsedSections, setCollapsedSections] = useState({
    price: false, luggage: false, vehicle: false, preferences: false, verification: false
  });

  // Build full address string
  const buildAddress = (addr) => {
    const parts = [];
    if (addr.street) parts.push(addr.street);
    if (addr.number) parts.push(addr.number);
    if (addr.city) parts.push(addr.city);
    return parts.join(', ');
  };

  const handleNotifyMe = async () => {
    setNotifyMeLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/rides/search/subscribe`, {
        from_city: departure.city || '',
        to_city: arrival.city || '',
        from_lat: departureCoords?.lat || null,
        from_lng: departureCoords?.lng || null,
        to_lat: arrivalCoords?.lat || null,
        to_lng: arrivalCoords?.lng || null,
        travel_date: date || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNotifyMeSaved(true);
    } catch {
      alert(t('search.notifyMe.error'));
    } finally {
      setNotifyMeLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      // Phase A2 — Use corridor endpoint when both coords are known.
      // Falls back to legacy point-to-point /rides/search otherwise.
      const canCorridor = useCorridor && departureCoords && arrivalCoords;
      let response;
      if (canCorridor) {
        const corridorParams = {
          from_lat: departureCoords.lat, from_lng: departureCoords.lng,
          to_lat: arrivalCoords.lat,     to_lng: arrivalCoords.lng,
          pickup_radius_km: pickupRadiusKm,
          dropoff_radius_km: dropoffRadiusKm,
        };
        if (date) corridorParams.date = date;
        if (dateToleranceDays > 0) corridorParams.date_tolerance_days = dateToleranceDays;
        if (maxDetourKm > 0) corridorParams.max_detour_km = maxDetourKm;
        response = await axios.get(`${API_URL}/rides/search/corridor`, { params: corridorParams });
        console.log('[RideSearch] Corridor query:', corridorParams, 'Results:', response.data.rides?.length || 0);
      } else {
        const params = {};
        const fromAddr = buildAddress(departure);
        const toAddr = buildAddress(arrival);
        if (fromAddr) params.from_city = departure.city || fromAddr;
        if (toAddr) params.to_city = arrival.city || toAddr;
        if (date) params.date = date;
        if (time) params.time = time;
        if (dateToleranceDays > 0) params.date_tolerance_days = dateToleranceDays;
        if (departureCoords) { params.from_lat = departureCoords.lat; params.from_lng = departureCoords.lng; }
        if (arrivalCoords) { params.to_lat = arrivalCoords.lat; params.to_lng = arrivalCoords.lng; }
        response = await axios.get(`${API_URL}/rides/search`, { params });
        console.log('[RideSearch] Legacy query:', params, 'Results:', response.data.rides?.length || 0);
      }
      setRides(response.data.rides || []);
      setFilteredRides(response.data.rides || []);
      setHasSearched(true);
      setNotifyMeSaved(false);

      // Also register as a guest on Socket.IO for live matching
      emitGuestSearch();
    } catch (error) {
      console.error('Search failed:', error);
      alert(t('search.searchError') + ': ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // ── Socket.IO REMOVED — HTTP-only search (no zombie connections) ──
  // Live matching disabled until 360dialog WhatsApp integration is complete

  // Emit guest:search when user has coords and searches
  const emitGuestSearch = useCallback(() => {
    if (!socketRef.current?.connected) return;
    if (!departureCoords || !arrivalCoords) return;

    socketRef.current.emit('guest:search', {
      user_id: user?.id || user?.user_id,
      name: user?.name || '',
      pickup_lat: departureCoords.lat,
      pickup_lng: departureCoords.lng,
      pickup_address: departure.city,
      dropoff_lat: arrivalCoords.lat,
      dropoff_lng: arrivalCoords.lng,
      dropoff_address: arrival.city,
      max_detour_km: 5,
    });
    console.log('[Socket.IO] Emitted guest:search');
  }, [departureCoords, arrivalCoords, departure.city, arrival.city, user]);

  // Load all rides on mount
  // ── Sprint 5 — Deep-Link Auto-Submit ────────────────────────────────
  // If the user lands on /search?from_city=Berlin&to_city=Hamburg&date=...
  // (e.g. from a push notification or email), pre-fill the form fields AND
  // fire a search automatically. No `searchParams` in the dep array → only
  // runs on first mount; URL changes via in-app navigation are handled by
  // the existing button submit.
  useEffect(() => {
    const qFrom = searchParams.get('from_city');
    const qTo   = searchParams.get('to_city');
    const qDate = searchParams.get('date');
    const qTime = searchParams.get('time');
    const qFromLat = parseFloat(searchParams.get('from_lat') || '');
    const qFromLng = parseFloat(searchParams.get('from_lng') || '');
    const qToLat   = parseFloat(searchParams.get('to_lat')   || '');
    const qToLng   = parseFloat(searchParams.get('to_lng')   || '');

    const hasMinimum = qFrom && qTo;
    if (hasMinimum) {
      setDeparture(prev => ({ ...prev, city: qFrom }));
      setArrival(prev => ({ ...prev, city: qTo }));
      if (qDate) setDate(qDate);
      if (qTime) setTime(qTime);
      if (Number.isFinite(qFromLat) && Number.isFinite(qFromLng)) {
        setDepartureCoords({ lat: qFromLat, lng: qFromLng });
      }
      if (Number.isFinite(qToLat) && Number.isFinite(qToLng)) {
        setArrivalCoords({ lat: qToLat, lng: qToLng });
      }
      // Defer until after state has been committed (next tick)
      setTimeout(() => { handleSearch(); }, 0);
    } else {
      // legacy "show whatever's available" first paint
      handleSearch();
    }
    // eslint-disable-next-line
  }, []);

  // Client-side filter logic
  useEffect(() => {
    let filtered = [...rides];
    if (priceRange) filtered = filtered.filter(r => r.price_per_seat >= priceRange[0] && r.price_per_seat <= priceRange[1]);
    if (suitcaseAllowed) filtered = filtered.filter(r => r.luggage?.suitcase_allowed);
    if (bicycleAllowed) filtered = filtered.filter(r => r.luggage?.bicycle_allowed);
    if (skiAllowed) filtered = filtered.filter(r => r.luggage?.ski_allowed);
    if (acRequired) filtered = filtered.filter(r => r.vehicle_info?.ac);
    if (spaciousRequired) filtered = filtered.filter(r => r.vehicle_info?.spacious);
    if (nonSmokerOnly) filtered = filtered.filter(r => !r.preferences?.smoking);
    if (petsAllowed) filtered = filtered.filter(r => r.preferences?.pets_allowed);
    if (verifiedOnly) filtered = filtered.filter(r => r.driver?.verified);
    if (minRating > 1) filtered = filtered.filter(r => (r.driver?.rating || 0) >= minRating);
    setFilteredRides(filtered);
  }, [rides, priceRange, suitcaseAllowed, bicycleAllowed, skiAllowed, acRequired, spaciousRequired, nonSmokerOnly, petsAllowed, verifiedOnly, minRating]);

  const toggleSection = (section) => setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  const handleAdvancedFilterChange = (key, value) => setAdvancedFilters(prev => ({ ...prev, [key]: value }));

  // Stripe "Book & Pay" flow
  const handleBookRide = async (ride, seats = 1) => {
    const token = localStorage.getItem('token');
    if (!token) {
      if (window.confirm(t('stripe.loginRequired'))) navigate('/login');
      return;
    }
    if (!user?.identity_verified) { alert(t('verification.required')); return; }

    seats = parseInt(seats, 10);
    if (isNaN(seats) || seats < 1) return;
    if (seats > ride.seats_available) {
      alert(t('stripe.notEnoughSeats', { available: ride.seats_available }));
      return;
    }

    setBookingLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/stripe/book-and-pay`,
        { ride_id: ride.ride_id, seats_booked: seats, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.data.checkout_url) window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Booking error:', error);
      alert(error.response?.data?.detail || t('stripe.bookingError'));
    } finally {
      setBookingLoading(false);
    }
  };

  // Cash-Flow Sprint — atomic Hin+Rück bundle booking
  const handleBookBundle = async ({ outboundId, returnId, totalPrice, savingsPct, forwardRide, returnRide }) => {
    const token = localStorage.getItem('token');
    if (!token) {
      if (window.confirm(t('stripe.loginRequired'))) navigate('/login');
      return;
    }
    if (!user?.identity_verified) { alert(t('verification.required')); return; }

    const seatsAvail = Math.min(
      forwardRide?.seats_available ?? 1,
      returnRide?.seats_available ?? 1,
    );
    if (seatsAvail < 1) {
      alert(t('stripe.notEnoughSeats', { available: 0 }));
      return;
    }

    setBookingLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/bookings/round-trip`,
        {
          outbound_ride_id: outboundId,
          return_ride_id: returnId,
          seats_booked: 1,
          origin_url: window.location.origin,
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      const data = response.data || {};
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      // Stripe not configured — show success notice with bundle summary
      const bundle = data.bundle;
      if (bundle) {
        alert(t('bundle.bookedNoStripe', {
          id: bundle.id?.slice(0, 8),
          fee: Number(bundle.total_service_fee).toFixed(2),
          saved: bundle.savings_pct,
        }));
        navigate('/my-rides');
      }
    } catch (error) {
      console.error('Bundle booking error:', error);
      alert(error.response?.data?.detail || t('stripe.bookingError'));
    } finally {
      setBookingLoading(false);
    }
  };

  const handleMapSelect = (data) => {
    const city = data.city || '';
    const road = data.road || '';
    const house = data.house_number || '';
    const displayCity = road ? `${road} ${house}, ${city}`.trim() : city;
    if (mapPickerOpen === 'departure') {
      setDeparture({ city: displayCity || data.display_name, street: road, number: house });
      setDepartureCoords({ lat: data.lat, lng: data.lng });
    } else if (mapPickerOpen === 'arrival') {
      setArrival({ city: displayCity || data.display_name, street: road, number: house });
      setArrivalCoords({ lat: data.lat, lng: data.lng });
    }
  };

  const handleAutocompleteDeparture = (suggestion) => {
    setDepartureCoords({ lat: suggestion.lat, lng: suggestion.lng });
    if (suggestion.road) setDeparture(prev => ({ ...prev, street: suggestion.road, number: suggestion.house_number || '' }));
  };

  const handleAutocompleteArrival = (suggestion) => {
    setArrivalCoords({ lat: suggestion.lat, lng: suggestion.lng });
    if (suggestion.road) setArrival(prev => ({ ...prev, street: suggestion.road, number: suggestion.house_number || '' }));
  };

  const fromFull = buildAddress(departure);
  const toFull = buildAddress(arrival);

  return (
    <div className="search-page">
      <div className="search-bar-container">
        {/* ---- DEPARTURE ---- */}
        <div className="address-section" data-testid="departure-section">
          <label className="address-label">
            <MapPin size={16} className="label-icon label-icon--green" />
            {t('common.from')}
          </label>
          <div className="address-row-with-map">
            <div className="address-row" style={{ flex: 1 }}>
              <AddressAutocomplete
                value={departure.city}
                onChange={(val) => setDeparture({ ...departure, city: val })}
                onSelect={handleAutocompleteDeparture}
                placeholder={t('search.city') || 'Stadt / Adresse'}
                testId="departure-city"
              />
            </div>
            <button
              type="button"
              className="map-select-btn"
              data-testid="departure-map-picker-btn"
              onClick={() => setMapPickerOpen('departure')}
              title="Auf Karte wählen"
            >
              <Map size={18} />
            </button>
          </div>
          <div className="address-detail-row">
            <div className="form-group">
              <label>{t('search.street') || 'Straße'}</label>
              <div className="input-wrapper">
                <MapPin size={18} />
                <input
                  type="text"
                  value={departure.street}
                  onChange={(e) => setDeparture({ ...departure, street: e.target.value })}
                  placeholder={t('search.street') || 'Straße'}
                  data-testid="departure-street"
                />
              </div>
            </div>
            <div className="form-group form-group--nr">
              <label>{t('search.houseNumber') || 'Nr.'}</label>
              <div className="input-wrapper">
                <input
                  type="text"
                  value={departure.number}
                  onChange={(e) => setDeparture({ ...departure, number: e.target.value })}
                  placeholder="Nr."
                  data-testid="departure-number"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ---- ARRIVAL ---- */}
        <div className="address-section" data-testid="arrival-section">
          <label className="address-label">
            <MapPin size={16} className="label-icon label-icon--red" />
            {t('common.to')}
          </label>
          <div className="address-row-with-map">
            <div className="address-row" style={{ flex: 1 }}>
              <AddressAutocomplete
                value={arrival.city}
                onChange={(val) => setArrival({ ...arrival, city: val })}
                onSelect={handleAutocompleteArrival}
                placeholder={t('search.city') || 'Stadt / Adresse'}
                testId="arrival-city"
              />
            </div>
            <button
              type="button"
              className="map-select-btn"
              data-testid="arrival-map-picker-btn"
              onClick={() => setMapPickerOpen('arrival')}
              title="Auf Karte wählen"
            >
              <Map size={18} />
            </button>
          </div>
          <div className="address-detail-row">
            <div className="form-group">
              <label>{t('search.street') || 'Straße'}</label>
              <div className="input-wrapper">
                <MapPin size={18} />
                <input
                  type="text"
                  value={arrival.street}
                  onChange={(e) => setArrival({ ...arrival, street: e.target.value })}
                  placeholder={t('search.street') || 'Straße'}
                  data-testid="arrival-street"
                />
              </div>
            </div>
            <div className="form-group form-group--nr">
              <label>{t('search.houseNumber') || 'Nr.'}</label>
              <div className="input-wrapper">
                <input
                  type="text"
                  value={arrival.number}
                  onChange={(e) => setArrival({ ...arrival, number: e.target.value })}
                  placeholder="Nr."
                  data-testid="arrival-number"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ---- DATE & TIME ---- */}
        <div className="date-row">
          <Calendar size={18} className="label-icon" />
          <input
            data-testid="search-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="date-input"
          />
          <input
            data-testid="search-time"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="date-input"
          />
        </div>

        {/* Phase A1 — Datums-Toleranz für Suche (symmetrisch zu Anbieten) */}
        <div className="form-group" data-testid="search-date-tolerance-group" style={{ marginTop: 8 }}>
          <label style={{ fontSize: 13, color: '#475569', fontWeight: 600, display: 'block', marginBottom: 4 }}>
            {t('search.dateTolerance', 'Datums-Toleranz')}
          </label>
          <select
            data-testid="search-date-tolerance"
            value={dateToleranceDays}
            onChange={(e) => setDateToleranceDays(parseInt(e.target.value, 10))}
            style={{
              width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8,
              border: '1px solid #e2e8f0', fontSize: 14, background: '#fff'
            }}
          >
            <option value={0}>{t('search.dateToleranceExact', 'Exakt am gewählten Datum')}</option>
            <option value={1}>{t('search.dateTolerance1', '±1 Tag')}</option>
            <option value={2}>{t('search.dateTolerance2', '±2 Tage')}</option>
            <option value={3}>{t('search.dateTolerance3', '±3 Tage')}</option>
            <option value={4}>{t('search.dateTolerance4', '±4 Tage')}</option>
            <option value={5}>{t('search.dateTolerance5', '±5 Tage')}</option>
          </select>
          {date && dateToleranceDays > 0 && (
            <div data-testid="search-date-tolerance-preview" style={{
              marginTop: 6, padding: '6px 10px', borderRadius: 6,
              background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)',
              fontSize: 12, color: '#15803d', fontWeight: 500
            }}>
              {(() => {
                const d = new Date(date);
                const from = new Date(d); from.setDate(d.getDate() - dateToleranceDays);
                const to = new Date(d); to.setDate(d.getDate() + dateToleranceDays);
                const fmt = (x) => x.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
                return t('search.dateToleranceRangePreview', {
                  from: fmt(from), to: fmt(to),
                  defaultValue: `Findet Fahrten zwischen ${fmt(from)} und ${fmt(to)}`
                });
              })()}
            </div>
          )}
        </div>

        {/* Phase A2 — Korridor-Radius (Pickup/Dropoff Slider) */}
        <div className="form-group" data-testid="search-corridor-group" style={{ marginTop: 10 }}>
          <label style={{ fontSize: 13, color: '#475569', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                data-testid="search-use-corridor"
                checked={useCorridor}
                onChange={(e) => setUseCorridor(e.target.checked)}
              />
              {t('search.useCorridor', 'Suche entlang der Route')}
            </span>
          </label>
          {useCorridor && (
            <>
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  {t('search.pickupRadius', 'Umkreis Start')}
                </span>
                <span style={{
                  fontSize: 12, fontWeight: 700, color: '#2563eb',
                  padding: '2px 8px', borderRadius: 999, background: 'rgba(37,99,235,0.08)'
                }} data-testid="search-pickup-radius-value">
                  {pickupRadiusKm} km
                </span>
              </div>
              <input
                type="range"
                data-testid="search-pickup-radius"
                min="1" max="20" step="1"
                value={pickupRadiusKm}
                onChange={(e) => setPickupRadiusKm(parseInt(e.target.value, 10))}
                style={{ width: '100%' }}
              />
              <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  {t('search.dropoffRadius', 'Umkreis Ziel')}
                </span>
                <span style={{
                  fontSize: 12, fontWeight: 700, color: '#2563eb',
                  padding: '2px 8px', borderRadius: 999, background: 'rgba(37,99,235,0.08)'
                }} data-testid="search-dropoff-radius-value">
                  {dropoffRadiusKm} km
                </span>
              </div>
              <input
                type="range"
                data-testid="search-dropoff-radius"
                min="1" max="20" step="1"
                value={dropoffRadiusKm}
                onChange={(e) => setDropoffRadiusKm(parseInt(e.target.value, 10))}
                style={{ width: '100%' }}
              />
            </>
          )}
        </div>

        {/* Phase B — Maximaler Umweg (passenger-side detour cap) */}
        <div className="form-group" data-testid="search-detour-group" style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: '#475569', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {t('search.maxDetour', 'Maximaler Umweg')}
            </label>
            <span
              data-testid="search-max-detour-value"
              style={{
                fontSize: 12, fontWeight: 700,
                color: maxDetourKm === 0 ? '#64748b' : '#16a34a',
                padding: '2px 10px', borderRadius: 999,
                background: maxDetourKm === 0 ? 'rgba(100,116,139,0.10)' : 'rgba(22,163,74,0.10)',
              }}
            >
              {maxDetourKm === 0
                ? t('search.maxDetourAny', 'beliebig')
                : `≤ ${maxDetourKm} km`}
            </span>
          </div>
          <input
            type="range"
            data-testid="search-max-detour"
            min="0" max="20" step="1"
            value={maxDetourKm}
            onChange={(e) => setMaxDetourKm(parseInt(e.target.value, 10))}
            style={{ width: '100%', marginTop: 6 }}
            aria-label={t('search.maxDetour', 'Maximaler Umweg')}
          />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
            {t('search.maxDetourHelp', 'Filtere Fahrten, deren Korridor max. so weit von deiner Route abweichen darf.')}
          </div>
        </div>

        {/* ---- SEARCH BUTTON ---- */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            data-testid="search-submit-btn"
            className="search-button-main"
            onClick={handleSearch}
            disabled={loading}
            style={{ flex: 1 }}
          >
            <Search size={22} />
            {loading ? t('common.loading') : t('common.search')}
          </button>
          <div
            data-testid="socket-status"
            title={socketConnected ? 'Live-Matching aktiv' : 'Live-Matching getrennt'}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.35rem',
              padding: '0.5rem 0.75rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600,
              background: socketConnected ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              color: socketConnected ? '#15803d' : '#dc2626',
              border: `1px solid ${socketConnected ? '#86efac' : '#fca5a5'}`,
              whiteSpace: 'nowrap',
            }}
          >
            <Wifi size={14} />
            <span className="live-badge-text">Live</span>
          </div>
        </div>

        {/* ---- FAIRNESS RICHTPREIS (nach Klick auf Suchen, direkt unter dem Button) ---- */}
        {hasSearched && departureCoords && arrivalCoords && (
          <RoutePriceEstimate
            fromCoords={departureCoords}
            toCoords={arrivalCoords}
          />
        )}

        <AdvancedFilters
          filters={advancedFilters}
          onFilterChange={handleAdvancedFilterChange}
          onApply={() => {}}
          onReset={() => setAdvancedFilters({})}
        />

        {/* ---- ROUTE PREVIEW MAP ---- */}
        {(departureCoords || departure.city) && (arrivalCoords || arrival.city) && (
          <div data-testid="search-route-map" style={{ marginTop: '1rem', height: '400px', borderRadius: '12px', overflow: 'hidden', border: '2px solid #e2e8f0' }}>
            <RideMap
              startCity={buildAddress(departure)}
              destinationCity={buildAddress(arrival)}
              startCoords={departureCoords}
              endCoords={arrivalCoords}
              showETA={true}
            />
          </div>
        )}
      </div>

      <div className="search-content">
        <aside className="filters-sidebar">
          <h2>{t('search.filters.title').toUpperCase()}</h2>

          {/* Price Filter */}
          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleSection('price')}>
              <h3>{t('search.filters.price')}</h3>
              {collapsedSections.price ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
            </div>
            {!collapsedSections.price && (
              <div className="filter-content">
                <div className="price-range-labels"><span>{priceRange[0]}€</span><span>{priceRange[1]}€</span></div>
                <input type="range" min="0" max="100" value={priceRange[1]}
                  onChange={(e) => setPriceRange([0, parseInt(e.target.value)])} className="price-slider" />
              </div>
            )}
          </div>

          {/* Luggage Filter */}
          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleSection('luggage')}>
              <h3>{t('createRide.sections.luggage')}</h3>
              {collapsedSections.luggage ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
            </div>
            {!collapsedSections.luggage && (
              <div className="filter-content">
                <label className="filter-checkbox"><input type="checkbox" checked={suitcaseAllowed} onChange={(e) => setSuitcaseAllowed(e.target.checked)} /> {t('createRide.luggage.suitcase')}</label>
                <label className="filter-checkbox"><input type="checkbox" checked={bicycleAllowed} onChange={(e) => setBicycleAllowed(e.target.checked)} /> {t('createRide.luggage.bicycle')}</label>
                <label className="filter-checkbox"><input type="checkbox" checked={skiAllowed} onChange={(e) => setSkiAllowed(e.target.checked)} /> {t('createRide.luggage.ski')}</label>
              </div>
            )}
          </div>

          {/* Vehicle Filter */}
          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleSection('vehicle')}>
              <h3>{t('createRide.sections.vehicle')}</h3>
              {collapsedSections.vehicle ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
            </div>
            {!collapsedSections.vehicle && (
              <div className="filter-content">
                <label className="filter-checkbox"><input type="checkbox" checked={acRequired} onChange={(e) => setAcRequired(e.target.checked)} /> {t('createRide.vehicle.ac')}</label>
                <label className="filter-checkbox"><input type="checkbox" checked={spaciousRequired} onChange={(e) => setSpaciousRequired(e.target.checked)} /> {t('createRide.vehicle.spacious')}</label>
              </div>
            )}
          </div>

          {/* Preferences Filter */}
          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleSection('preferences')}>
              <h3>{t('createRide.sections.preferences')}</h3>
              {collapsedSections.preferences ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
            </div>
            {!collapsedSections.preferences && (
              <div className="filter-content">
                <label className="filter-checkbox"><input type="checkbox" checked={nonSmokerOnly} onChange={(e) => setNonSmokerOnly(e.target.checked)} /> {t('search.filters.noSmoking')}</label>
                <label className="filter-checkbox"><input type="checkbox" checked={petsAllowed} onChange={(e) => setPetsAllowed(e.target.checked)} /> {t('search.filters.petsAllowed')}</label>
              </div>
            )}
          </div>

          {/* Driver Verification */}
          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleSection('verification')}>
              <h3>{t('rideDetails.verified')}</h3>
              {collapsedSections.verification ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
            </div>
            {!collapsedSections.verification && (
              <div className="filter-content">
                <label className="filter-checkbox"><input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} /> {t('admin.users.verified')}</label>
                <div className="rating-filter">
                  <span>{t('search.filters.rating')}: {minRating}</span>
                  <input type="range" min="1" max="5" step="0.5" value={minRating} onChange={(e) => setMinRating(parseFloat(e.target.value))} />
                </div>
              </div>
            )}
          </div>
        </aside>

        <main className="search-results">
          <h2>{filteredRides.length} {t('search.ridesAvailable').toUpperCase()}</h2>

          {loading ? (
            <div className="loading-state"><div className="spinner" /><p>{t('common.loading')}</p></div>
          ) : filteredRides.length === 0 && hasSearched ? (
            <div data-testid="no-results-notify-me" style={{
              background: 'var(--glass-bg, rgba(255,255,255,0.82))',
              backdropFilter: 'var(--glass-blur, blur(16px))',
              WebkitBackdropFilter: 'var(--glass-blur, blur(16px))',
              border: 'var(--glass-border, 1px solid rgba(255,255,255,0.45))',
              borderRadius: 'var(--glass-radius, 20px)',
              boxShadow: 'var(--glass-shadow, 0 8px 32px rgba(0,0,0,0.08))',
              padding: '2rem', textAlign: 'center',
            }}>
              <MapPin size={40} style={{ color: '#94a3b8', margin: '0 auto 1rem' }} />
              <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem' }}>
                {t('search.notifyMe.title')}
              </h3>
              <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1.25rem', maxWidth: '400px', margin: '0 auto 1.25rem' }}>
                {t('search.notifyMe.description')}
              </p>
              {notifyMeSaved ? (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.75rem 1.5rem', borderRadius: '12px',
                  background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a',
                  fontWeight: 600, fontSize: '0.9rem',
                }}>
                  <Bell size={18} />
                  {t('search.notifyMe.saved')}
                </div>
              ) : (
                <button
                  onClick={handleNotifyMe}
                  disabled={notifyMeLoading || !user}
                  data-testid="notify-me-button"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.85rem 2rem', borderRadius: '12px',
                    background: '#2563eb', color: '#fff', border: 'none',
                    fontWeight: 700, fontSize: '1rem', cursor: 'pointer',
                    opacity: notifyMeLoading ? 0.6 : 1,
                    transition: 'all 0.2s',
                  }}
                >
                  <Bell size={18} />
                  {notifyMeLoading ? t('search.notifyMe.saving') : t('search.notifyMe.button')}
                </button>
              )}
            </div>
          ) : filteredRides.length === 0 ? (
            <div className="no-results"><p>{t('search.noResults')}</p></div>
          ) : (
            <div className="rides-list">
              {(() => {
                const { solo, pairs } = groupRoundTrips(filteredRides);
                return (
                  <>
                    {pairs.map(({ forward, return: ret }) => (
                      <RoundTripBundle
                        key={`bundle-${forward.ride_id || forward.id}`}
                        forwardRide={forward}
                        returnRide={ret}
                        onClick={(ride) => setSelectedRide(ride)}
                        onViewRoute={(r) => setCorridorRideId(r.ride_id || r.id)}
                        onBookBundle={handleBookBundle}
                        bundleLoading={bookingLoading}
                      />
                    ))}
                    {solo.map((ride) => (
                      <div key={ride.ride_id} className="ride-item-container">
                        <RideCard
                          ride={ride}
                          onClick={() => setSelectedRide(ride)}
                          onViewRoute={(r) => setCorridorRideId(r.ride_id || r.id)}
                        />
                        <div className="preview-map-box" style={{ height: '120px', borderRadius: '0 0 12px 12px', overflow: 'hidden', marginTop: '-12px', marginBottom: '20px', border: '1px solid #e2e8f0', borderTop: 'none' }}>
                          <RideMapPreview startCity={ride.start_city} destinationCity={ride.end_city || ride.destination_city} />
                        </div>
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          )}
        </main>
      </div>

      {selectedRide && (
        <RideDetailsModal
          ride={selectedRide}
          onClose={() => setSelectedRide(null)}
          onBook={handleBookRide}
          bookingLoading={bookingLoading}
        />
      )}

      <MapPickerModal
        open={mapPickerOpen !== null}
        onClose={() => setMapPickerOpen(null)}
        onSelect={handleMapSelect}
        label={mapPickerOpen === 'departure' ? (t('common.from') + ' auf Karte wählen') : (t('common.to') + ' auf Karte wählen')}
      />

      {/* Phase A3 — Corridor route overlay */}
      {corridorRideId && (
        <RouteCorridorOverlay
          rideId={corridorRideId}
          onClose={() => setCorridorRideId(null)}
        />
      )}

      {/* ── LIVE MATCH POPUP ── */}
      {showLivePopup && liveMatches.length > 0 && (
        <div className="live-match-overlay" data-testid="live-match-popup">
          <div className="live-match-card">
            <div className="live-match-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div className="live-pulse" />
                <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1e40af' }}>
                  {t('search.liveMatch') || 'Live Match gefunden!'}
                </span>
              </div>
              <button
                className="live-match-close"
                onClick={() => setShowLivePopup(false)}
                data-testid="live-match-close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="live-match-list">
              {liveMatches.map((match, i) => (
                <div key={i} className="live-match-item" data-testid={`live-match-${i}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>
                        {match.driver_name || 'Fahrer'}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.15rem' }}>
                        {match.start_city} → {match.end_city}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {match.total_duration_min && (
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>
                          ~{Math.round(match.total_duration_min)} Min.
                        </div>
                      )}
                      {match.total_distance_km && (
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          {match.total_distance_km} km
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.8rem', color: '#64748b' }}>
                    <span>Umweg: {match.detour_km} km</span>
                    <span>Plätze: {match.max_seats}</span>
                    {match.vehicle && <span>{match.vehicle}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
